/**
 * Mobile product card with inactive / active states.
 * Inactive: first image only, no dots, no cart/wishlist. Active: carousel + dots + actions.
 * Tap / click → PDP immediately. Multi-image + inactive: as soon as the finger moves past tap slop
 * (touchmove), the carousel starts; touchend only blocks the synthetic click for that gesture.
 */
import { memo, useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCartOutline, IoCheckmark } from 'react-icons/io5';
import { ReactComponent as HeartImg } from '../assets/heart.svg';
import newTag from '../assets/new.svg';
import axios from 'axios';
import { useProfile } from '../../../Contexts/ProfileContext';
import { useToast } from '../../../Contexts/ToastContext';
import { UseInterval } from '../UseInterval';
import './ProductCardCarouselStack.scss';

const SLIDE_INTERVAL_MS = 2000;
/** Below this finger travel (px) from touchstart we treat as “click intent” → PDP via synthetic click. */
const VIEWPORT_TAP_SLOP_PX = 12;

function getGuestCartProductIds() {
    try {
        const raw = localStorage.getItem('guestCart');
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return (arr || []).map((i) => String(i.product_id));
    } catch {
        return [];
    }
}

function ProductCardCarouselStackComponent({ product, isActive: controlledActive, onActivate, gridIndex }) {
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const { id, urls: images = [], title, price, brand, mrp, rating, reviewCount, isNew } = product;
    const slides = images.length ? [...images, images[0]] : [];
    const count = slides.length;
    const firstImage = images[0];

    const [localActive, setLocalActive] = useState(false);
    const isActive = typeof controlledActive === 'boolean' ? controlledActive : localActive;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [addingToCart, setAddingToCart] = useState(null);
    const [addedToCart, setAddedToCart] = useState(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (isGuest && getGuestCartProductIds().includes(String(id))) return String(id);
        return null;
    });
    const [addingToWishlist, setAddingToWishlist] = useState(null);
    const [addedToWishlist, setAddedToWishlist] = useState(null);

    const viewportRef = useRef(null);

    const markProductImagesLoaded = () => {
        const root = viewportRef.current;
        if (!root) return;
        root.querySelectorAll('img.product-card-stack__media-img').forEach((el) => {
            if (el.complete && el.naturalWidth > 0) {
                el.classList.add('product-card-stack__img--loaded');
            }
        });
    };

    useLayoutEffect(() => {
        markProductImagesLoaded();
    }, [firstImage, id, isActive, currentIndex, count]);

    useEffect(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (!isGuest) return;
        const check = () => {
            if (getGuestCartProductIds().includes(String(id))) setAddedToCart(String(id));
        };
        check();
        window.addEventListener('guestCartUpdated', check);
        return () => window.removeEventListener('guestCartUpdated', check);
    }, [id]);

    const handleActivate = useCallback(() => {
        if (onActivate) onActivate(id);
        else setLocalActive(true);
    }, [id, onActivate]);

    const handleCardClick = () => {
        navigate(`/specific/${id}`);
    };

    /**
     * Multi-image + inactive: small movement = tap → touchend does nothing → click opens PDP.
     * Past tap slop → start carousel on touchmove (finger still down), not only on finger lift.
     * Touchend preventDefault only when a carousel gesture happened, to block synthetic click.
     */
    useEffect(() => {
        const el = viewportRef.current;
        if (!el || isActive || images.length <= 1) return;

        let sx = 0;
        let sy = 0;
        let maxExcursion = 0;
        let carouselStarted = false;

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            sx = e.touches[0].clientX;
            sy = e.touches[0].clientY;
            maxExcursion = 0;
            carouselStarted = false;
        };

        const onTouchMove = (e) => {
            if (e.touches.length !== 1) return;
            const x = e.touches[0].clientX;
            const y = e.touches[0].clientY;
            const d = Math.hypot(x - sx, y - sy);
            if (d > maxExcursion) maxExcursion = d;
            if (!carouselStarted && maxExcursion > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
        };

        const onTouchEnd = (e) => {
            if (e.changedTouches.length !== 1) return;
            const t = e.changedTouches[0];
            const endDist = Math.hypot(t.clientX - sx, t.clientY - sy);
            const furthest = Math.max(maxExcursion, endDist);
            if (!carouselStarted && furthest > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
            if (carouselStarted) {
                e.preventDefault();
            }
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: false });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [isActive, images.length, handleActivate]);

    UseInterval(() => {
        if (!isActive || count <= 1) return;
        setCurrentIndex((i) => (i + 1) % count);
    }, isActive && count > 1 ? SLIDE_INTERVAL_MS : null);

    const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (addedToCart === String(id)) {
            showToast({ message: 'Already in cart' });
            return;
        }
        setAddingToCart(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const existingIndex = guestCart.findIndex((item) => String(item.product_id) === String(id));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + 1;
                } else {
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images,
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                        selectedQty: 1,
                        size: { M: 10 },
                        availableSizes: ['M'],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: 10,
                    });
                }
                localStorage.setItem('guestCart', JSON.stringify(guestCart));
                setAddedToCart(String(id));
                window.dispatchEvent(new Event('guestCartUpdated'));
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            } else {
                const token = localStorage.getItem('token');
                if (!token) {
                    showToast({
                        message: 'Please login to add items to cart',
                        variant: 'info',
                        action: { label: 'Login', onClick: () => navigate('/auth/login') },
                    });
                    return;
                }
                const pincode = '110001';
                await axios.post(
                    `/cart/${id}`,
                    { selectedSize: 'M', selectedQty: 1, pincode },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAddedToCart(String(id));
                await fetchProfile();
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            }
        } catch (err) {
            console.error(err);
            showToast({ message: err.response?.data?.message || 'Could not add to cart. Try again.' });
        } finally {
            setAddingToCart(null);
        }
    };

    const handleAddToWishlist = async (e) => {
        e.stopPropagation();

        if (addedToWishlist === String(id)) {
            showToast({ message: 'Already in wishlist', action: { label: 'View wishlist', onClick: () => navigate('/wishlist') } });
            return;
        }

        setAddingToWishlist(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const exists = guestWishlist.some((item) => String(item.product_id) === String(id));
                if (!exists) {
                    guestWishlist.push({
                        _id: `guest-wish-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images || [],
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                    });
                    localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
                }
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Added to wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                setAddingToWishlist(null);
                showToast({ message: 'Please login to add items to wishlist' });
                return;
            }

            const config = {
                headers: { Authorization: `Bearer ${token}` },
            };

            await axios.post(
                `/products/wish-list/${id}`,
                { selectedSize: 'M' },
                config
            );

            setAddedToWishlist(String(id));
            setTimeout(() => setAddedToWishlist(null), 3000);
            await fetchProfile();
            showToast({
                message: 'Added to wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
        } catch (error) {
            if (error.response?.status === 400) {
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Already in wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
            } else {
                showToast({ message: 'Could not add to wishlist. Try again.' });
            }
        } finally {
            setAddingToWishlist(null);
        }
    };

    const discount = mrp != null && price != null && mrp > 0 ? Math.round((1 - price / mrp) * 100) : 0;
    /* Slider passes index: first row eager, rest lazy to reduce cold-load decode contention on iOS. */
    const listImageLoading =
        typeof gridIndex === 'number' ? (gridIndex < 4 ? 'eager' : 'lazy') : 'eager';
    const ratingValue = rating != null ? rating : 4.2;
    const ratingCountText =
        reviewCount != null
            ? (reviewCount >= 1000 ? `${(reviewCount / 1000).toFixed(1)}K` : reviewCount)
            : '4.2K';

    return (
        <div
            className={`product-card-stack ${isActive ? 'product-card-stack--active' : 'product-card-stack--inactive'}`}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        >
            <div className="product-card-stack__viewport" ref={viewportRef}>
                {!isActive ? (
                    /* Inactive: first image only, no carousel */
                    firstImage && (
                        <div className="product-card-stack__slide slide-current" style={{ zIndex: 1 }}>
                            <img
                                key={firstImage}
                                className="product-card-stack__media-img"
                                src={firstImage}
                                alt=""
                                loading={listImageLoading}
                                decoding="async"
                                onLoad={(e) => e.currentTarget.classList.add('product-card-stack__img--loaded')}
                            />
                        </div>
                    )
                ) : (
                    /* Active: carousel with opacity crossfade (no seam) */
                    slides.map((src, i) => {
                        const isCurrent = i === currentIndex;
                        return (
                            <div
                                key={`${id}-${i}`}
                                className="product-card-stack__slide product-card-stack__slide--crossfade"
                                style={{
                                    opacity: isCurrent ? 1 : 0,
                                    zIndex: isCurrent ? 2 : 1,
                                }}
                            >
                                <img
                                    className="product-card-stack__media-img"
                                    src={src}
                                    alt=""
                                    decoding="async"
                                    onLoad={(e) => e.currentTarget.classList.add('product-card-stack__img--loaded')}
                                />
                            </div>
                        );
                    })
                )}

                {/* New tag – always overlayed */}
                {firstImage && (
                    <div className="product-card-stack__new-tag" aria-hidden>
                        <img src={newTag} alt="New" />
                    </div>
                )}
                {/* Rating badge – only when card is inactive */}
                {firstImage && !isActive && (
                    <div className="product-card-stack__rating">
                        <span>{ratingValue} *</span>
                        <span>| {ratingCountText}</span>
                    </div>
                )}

                {/* Dots absolute at bottom of image – no extra height */}
                {isActive && count > 1 && (
                    <div className="product-card-stack__dots-wrap">
                        <ul className="product-card-stack__dots" aria-hidden>
                            {images.map((_, i) => (
                                <li key={i} className={currentIndex === i ? 'active' : ''}>
                                    <button type="button" aria-label={`Image ${i + 1}`} />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="product-card-stack__meta">
                <div className="product-card-stack__meta-row">
                    <div className="brand">{brand ?? '—'}</div>
                    {isActive && (
                        <div className="product-card-stack__actions-inline">
                            <button
                                type="button"
                                className={`action-cart ${addingToCart ? 'loading' : ''} ${addedToCart ? 'added' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleAddToCart(e); }}
                                aria-label="Add to cart"
                            >
                                {addingToCart ? <span className="spinner" /> : addedToCart ? <IoCheckmark size="1.25em" /> : <IoCartOutline size="1.25em" />}
                            </button>
                            <button
                                type="button"
                                className={`action-wishlist ${addingToWishlist ? 'loading' : ''} ${addedToWishlist ? 'added' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleAddToWishlist(e); }}
                                aria-label="Add to wishlist"
                            >
                                {addingToWishlist ? <span className="spinner" /> : addedToWishlist ? <IoCheckmark size="1.25em" /> : <HeartImg width="1.25em" height="1.25em" />}
                            </button>
                        </div>
                    )}
                </div>
                {title && <div className="product-card-stack__title">{title}</div>}
                <div className="offer-details">
                    {price != null && <span>Rs. {price}</span>}
                    {mrp != null && mrp > 0 && <span>Rs. {mrp}</span>}
                    {discount > 0 && <span>({discount}% OFF)</span>}
                </div>
            </div>
        </div>
    );
}

export const ProductCardCarouselStack = memo(ProductCardCarouselStackComponent);
